import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CopyPlanInput,
  CopyPlanResult,
  LessonPlan,
  PlanCell,
  PlanRow,
  PlanTemplate,
  Weekday,
  WeekKey,
} from '@td/shared';
import { get, post, patch, put, del } from '../lib/api';

const planKey = (week: WeekKey) => ['lesson-plan', week];

function useInvalidate() {
  const qc = useQueryClient();
  return (week: WeekKey) => {
    void qc.invalidateQueries({ queryKey: planKey(week) });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
  };
}

export function useLessonPlan(week: WeekKey) {
  return useQuery({
    queryKey: planKey(week),
    queryFn: () => get<LessonPlan>(`/lesson-plans?weekStart=${week}`),
    staleTime: 60_000,
  });
}

export function useSaveCell() {
  const qc = useQueryClient();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({
      week,
      rowId,
      weekday,
      values,
    }: {
      week: WeekKey;
      rowId: string;
      weekday: Weekday;
      values: PlanCell;
    }) => patch<LessonPlan>(`/lesson-plans/${week}/cell`, { rowId, weekday, values }),

    // Write straight into the cache so the table doesn't flicker on save.
    onSuccess: (data, vars) => {
      qc.setQueryData(planKey(vars.week), data);
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (_e, vars) => invalidate(vars.week),
  });
}

export function useSaveRows() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ week, rows }: { week: WeekKey; rows: PlanRow[] }) =>
      put<LessonPlan>(`/lesson-plans/${week}/rows`, { rows }),
    onSuccess: (data, vars) => {
      qc.setQueryData(planKey(vars.week), data);
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useSaveNotes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ week, notes }: { week: WeekKey; notes: string }) =>
      patch<LessonPlan>(`/lesson-plans/${week}/notes`, { notes }),
    onSuccess: (data, vars) => qc.setQueryData(planKey(vars.week), data),
  });
}

export function useCopyWeek() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      week,
      fromWeek,
      includeContent,
    }: {
      week: WeekKey;
      fromWeek: WeekKey;
      includeContent: boolean;
    }) => post<LessonPlan>(`/lesson-plans/${week}/copy-from`, { fromWeek, includeContent }),
    onSuccess: (data, vars) => {
      qc.setQueryData(planKey(vars.week), data);
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useClearWeek() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ week }: { week: WeekKey }) => post<LessonPlan>(`/lesson-plans/${week}/clear`),
    onSuccess: (data, vars) => {
      qc.setQueryData(planKey(vars.week), data);
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useCopyPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CopyPlanInput) => post<CopyPlanResult>('/lesson-plans/copy', input),
    onSuccess: (result, input) => {
      // The target week is authoritative in the response; the source week is
      // untouched, so only the target and the dashboard need refreshing.
      qc.setQueryData(planKey(input.to.week), result.plan);
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function usePlanTemplates() {
  return useQuery({
    queryKey: ['plan-templates'],
    queryFn: () => get<PlanTemplate[]>('/lesson-plan-templates'),
  });
}

export function useSaveTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; rows: PlanRow[] }) =>
      post<PlanTemplate>('/lesson-plan-templates', { ...input, isDefault: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['plan-templates'] }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del<void>(`/lesson-plan-templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['plan-templates'] }),
  });
}

export function useApplyTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, week }: { id: string; week: WeekKey }) =>
      post<LessonPlan>(`/lesson-plan-templates/${id}/apply`, { weekStart: week }),
    onSuccess: (data, vars) => qc.setQueryData(planKey(vars.week), data),
  });
}
